<script setup lang="ts">
import type { Form } from '~/types';
import type { FormError } from '@nuxt/ui';
//import { ProposalsBlocksPricing } from '#build/components';

// reCAPTCHA Enterprise global type declaration
declare const grecaptcha: {
  enterprise: {
    ready: (callback: () => void) => void
    execute: (siteKey: string, options: { action: string }) => Promise<string>
  }
}

const config = useRuntimeConfig();
const RECAPTCHA_TOKEN_URL = config.public.recaptchaTokenURL;
const RECAPTCHA_SITE_KEY = config.public.recaptchaSiteKey;

if (!RECAPTCHA_TOKEN_URL) {
  console.log('RECAPTCHA_TOKEN_URL', RECAPTCHA_TOKEN_URL);
  throw new Error('RECAPTCHA_TOKEN_URL is not set in environment variables.');
}

if (!RECAPTCHA_SITE_KEY) {
  console.log('RECAPTCHA_SITE_KEY', RECAPTCHA_SITE_KEY);
  throw new Error('RECAPTCHA_SITE_KEY is not set in environment variables.');
}

const props = defineProps<{
  form: Form;
}>();

const emit = defineEmits(['submit', 'update:modelValue']);

// Get query params to allow prefilling of form fields
const { query } = useRoute();
const formData = reactive({ ...query });
const loading = ref(false);
const error: any = ref(null);
const success = ref(false);

function getValidationRules(field: { [key: string]: any }) {
  if (typeof field.validation !== 'string') {
    return [];
  }

  return field.validation.split('|').map((rule: string) => rule.trim()).filter(Boolean);
}

function isEmptyValue(value: unknown) {
  return value === undefined || value === null || value === '' || value === false || (Array.isArray(value) && !value.length);
}

const validate = (state: any): FormError[] => {
  const errors: FormError[] = [];

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  for (const field of props.form.schema ?? []) {
    const name = field.name;

    if (!name) {
      continue;
    }

    const rules = getValidationRules(field);
    const value = state[name];

    if (rules.includes('required') && isEmptyValue(value)) {
      errors.push({ name, message: 'Required' });
      continue;
    }

    if (rules.includes('email') && !isEmptyValue(value) && !emailRegex.test(String(value))) {
      errors.push({ name, message: 'Invalid email' });
    }
  }

  return errors;
};

async function submitForm() {
  loading.value = true;

  if (validate(formData).length) {
    //create a string of all the error Paths with their Messages and assign to the error const
    error.value = validate(formData)
      .map((error) => `${error.name}: ${error.message}`)
      .join(', ');

    loading.value = false;
    return false;
  }

  //if we got here error.value should be false
  error.value = false;

  //do api validation from recaptcha here make server call
  grecaptcha.enterprise.ready(async function () {
    const action = 'submit'; //TODO: Modify this to pull the dynamicly from Consus.
    const token = await grecaptcha.enterprise.execute(config.public.recaptchaSiteKey, { action });

    // Add your logic to submit to your backend server here.
    console.log('\nGot Token, calling /api/recaptcha: ', token);

    const score = await $fetch('/api/recaptcha', {
      params: {
        token,
        action,
      },
    });

    console.info('\nscore', score);

    const numericScore = Number(score);
    if (numericScore > 0.5) {
      console.log('score is good');

      try {
        await useDirectusProxy({
            operation: 'createItem',
            collection: 'site_inbox',
            item: {
              data: formData,
              form: props.form.id,
            },
          });

        success.value = true;

        if (props.form.on_success === 'redirect') {
          return navigateTo(props.form.redirect_url);
        }
      } catch (err: any) {
        error.value = err;
      } finally {
        loading.value = false;
      }
    } else {
      console.log('score is bad');
      loading.value = false;
    }
  });
}

watch(
  formData,
  () => {
    emit('update:modelValue', formData);
    validate(formData);
  },
  { deep: true },
);
</script>
<template>
  <div v-auto-animate>
    <div class="mb-4">
      <VAlert v-if="error" type="error">Oops! {{ error }}</VAlert>
      <VAlert v-if="form.on_success === 'message' && success" type="success">
        <div class="items-center" v-html="form.success_message ?? 'Success! Your form has been submitted.'" />
        <div class="items-center">
          <UButton
            to="/"
            class="text-white dark:text-white bg-totem-pole ml-3 items-center"
            variant="solid"
            size="md"
            label="Back to Home"
          />
        </div>
        <!--  -->
      </VAlert>
    </div>

    <div>
      <FormCustom
        v-if="!success"
        :schema="props.form.schema"
        :form-key="props.form.key"
        :state="formData"
        :validate="validate"
        :submit-label="props.form.submit_label ?? 'Submit'"
        class="grid gap-6 md:grid-cols-6"
        :on-submit="submitForm"
      />
      <div id="recaptchadiv"/>
    </div>
  </div>
</template>
